import { useContext, useMemo } from "react";
import { participantSearchContext } from "./components/ParticipantSearchContext";
import { useInfiniteQuery } from "@tanstack/react-query";
import { userByIdOptions, usersInfiniteOptions } from "@pulse/api/queryApi";
import { queryClient } from "@pulse/queryClient";

export const useParticipantSearch = () => {
    const context = useContext(participantSearchContext);
    if (!context) {
        throw new Error("useParticipantSearch must be used within a SearchProvider");
    }
    return context;
};

/**
 *
 * @param search
 * @param customerId
 * @returns { users: { total: number; data: User[] }, fetchNextPage: () => void, hasNextPage: boolean | undefined, isFetchingNextPage: boolean }
 *
 * Hook to fetch users for infinite scrolling. The users are cached individually to avoid refetching singlular users to get their details later.
 */
export function useUsersCache(search: string, customerId?: number | null) {
    const {
        data: usersRaw,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteQuery({
        ...usersInfiniteOptions({
            queries: {
                customerId: customerId ? [customerId] : undefined,
                search,
                order: "asc",
                orderKey: "fullName",
                deleted: false,
                type: ["NORMAL"],
            },
            take: 100,
            enabled: !!customerId,
        }),
    });

    const usersFinished = useMemo(() => {
        const users = usersRaw?.pages.flatMap((page) => page.data);
        if (users) {
            users.forEach((user) => {
                queryClient.setQueryData(userByIdOptions({ id: user.id }).queryKey, user);
            });
        }
        return { total: usersRaw?.pages[0]?.total || 0, data: users || [] };
    }, [usersRaw]);

    return {
        users: usersFinished,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    };
}
